/**
 * Distributes a total amount across a list of items proportionally and precisely,
 * respecting the maximum allowed value per item (e.g., an invoice's adjusted amount due),
 * and avoiding floating-point precision errors.
 *
 * @template T - Type of items to receive amounts (e.g., InvoicePaymentDto).
 *
 * @param items - The list of items to distribute the total amount across.
 * @param getMax - A function that returns the maximum value allowed for each item (e.g., the adjusted amount due).
 * @param setValue - A function that applies the calculated amount to the item.
 * @param totalAmount - The total amount to be distributed among the items.
 *
 * @remarks
 * - The function works in cents (integer math) to avoid floating point issues.
 * - If there's a rounding remainder, it is distributed 1 cent at a time to the first items that can still receive more.
 * - The final result guarantees that the sum of amounts exactly matches the `totalAmount` provided.
 */
export function distributeAmountPrecisely<T>(
  items: T[],
  getMax: (item: T) => number,
  setValue: (item: T, value: number) => void,
  totalAmount: number
): void {
  const precision = 2;
  const factor = Math.pow(10, precision);

  // Convert to integer cents
  let remaining = Math.round(totalAmount * factor);
  const maxes = items.map((item) => Math.round(getMax(item) * factor));
  const applied = new Array(items.length).fill(0);

  // First pass: assign as much as allowed without exceeding max
  for (let i = 0; i < items.length; i++) {
    const toApply = Math.min(remaining, maxes[i]);
    applied[i] = toApply;
    remaining -= toApply;
  }

  // Distribute any leftover cents one-by-one to eligible items
  while (remaining > 0) {
    for (let i = 0; i < items.length && remaining > 0; i++) {
      if (applied[i] < maxes[i]) {
        applied[i]++;
        remaining--;
      }
    }
  }

  // Set final decimal values back to items
  for (let i = 0; i < items.length; i++) {
    setValue(items[i], applied[i] / factor);
  }
}
